import { forwardRef, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@libs/zapi-ui/shadcn";
import { AppButton } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import * as DsMenu from "@libs/zapi-ui/ds/components/dropdown-menu";
import * as DsPopover from "@libs/zapi-ui/ds/components/popover";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";

const DimensionPill = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ children, className, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={cn(
        "flex items-center gap-1 h-8 px-3 rounded-lg border border-innoveraZinc-150 bg-white",
        "text-[14px] leading-[13px] font-medium outline-hidden",
        rest.disabled
          ? "text-innoveraZinc-400"
          : "text-innoveraZinc-900 hover:border-innoveraZinc-200 transition-colors",
        className
      )}
    >
      {children}
    </button>
  )
);
DimensionPill.displayName = "DimensionPill";

type TProjectDimensionControlProps = {
  dimension: TOrganizationProjectDimension;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  // `design-system` renders the menu and the text popover with the design system, for a migrated part.
  appearance?: "default" | "design-system";
};

export function ProjectDimensionControl({
  dimension,
  value,
  onChange,
  disabled = false,
  appearance = "default",
}: TProjectDimensionControlProps) {
  const { t } = useTranslation("projectHome");
  const [isTextEditorOpen, setIsTextEditorOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pillContent = value || (
    <>
      <Plus className="size-4" weight="regular" />
      {t("projectSetup.dimensionAdd")}
    </>
  );

  const textInput = (
    <input
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") setIsTextEditorOpen(false);
      }}
      placeholder={t("projectSetup.dimensionAddValue")}
      className="flex-1 min-w-0 h-8 px-3 rounded-lg border border-innoveraZinc-150 bg-white text-[14px] leading-[13px] font-medium text-innoveraZinc-900 outline-hidden placeholder:text-innoveraZinc-300"
    />
  );

  if (appearance === "design-system") {
    if (dimension.type === "text") {
      return (
        <DsPopover.Popover open={isTextEditorOpen} onOpenChange={setIsTextEditorOpen}>
          <DsPopover.PopoverTrigger disabled={disabled} render={<DimensionPill />}>
            {pillContent}
          </DsPopover.PopoverTrigger>
          <DsPopover.PopoverContent
            align="start"
            sideOffset={4}
            initialFocus={inputRef}
            className="gap-0 rounded-xl w-[240px] p-2"
          >
            <div className="flex items-center gap-1">
              {textInput}
              <Button type="button" onClick={() => setIsTextEditorOpen(false)}>
                {t("projectSetup.dimensionSave")}
              </Button>
            </div>
          </DsPopover.PopoverContent>
        </DsPopover.Popover>
      );
    }
    return (
      <DsMenu.DropdownMenu>
        <DsMenu.DropdownMenuTrigger disabled={disabled} render={<DimensionPill />}>
          {pillContent}
        </DsMenu.DropdownMenuTrigger>
        <DsMenu.DropdownMenuContent
          align="start"
          sideOffset={8}
          className="w-auto min-w-48 rounded-xl p-1.5"
        >
          {dimension.options.map((option) => (
            <DsMenu.DropdownMenuItem key={option} onClick={() => onChange(option)}>
              {option}
            </DsMenu.DropdownMenuItem>
          ))}
        </DsMenu.DropdownMenuContent>
      </DsMenu.DropdownMenu>
    );
  }

  if (dimension.type === "text") {
    return (
      <Popover open={isTextEditorOpen} onOpenChange={setIsTextEditorOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <DimensionPill>{pillContent}</DimensionPill>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-[240px] p-2"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className="flex items-center gap-1">
            {textInput}
            <AppButton onClick={() => setIsTextEditorOpen(false)}>{t("projectSetup.dimensionSave")}</AppButton>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <DimensionPill>{pillContent}</DimensionPill>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {dimension.options.map((option) => (
          <DropdownMenuItem key={option} onClick={() => onChange(option)}>
            {option}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
